import { environment } from "@/environment/environment";
import { store } from "@/app/shared/stores/store";
import { appLocalStorage, appSessionStorage } from "../app-storage/app-storage";
import { ReloadHelper } from "../helper/reload-helper";
import { ServiceWorkerHelper } from "../helper/service-worker-helper";
import { userService } from "../user-service/user-service";
import { volateqApi } from "../volateq-api/volateq-api";
import type { DeployedBuild, ReloadAttempts } from "./types";

/** Poll interval of version.json. The file is a few bytes, so it can be checked often. */
const CHECK_INTERVAL_MS = 120000;

/**
 * A reload within this time after the page load (or after a back/forward cache restore)
 * looks like a slightly slow page load to the user, not like an interruption.
 */
const UNNOTICED_RELOAD_TIMEOUT_MS = 15000;

/** Seconds the user gets before a tab that is actively used reloads itself. */
const RELOAD_COUNTDOWN_SECONDS = 60;

/** Shared between the tabs, so that one tab detecting a deployment informs all others. */
const DEPLOYED_BUILD_ID_KEY = "deployedBuildId";

/** Per tab, survives the reload. Guards against reloading in a loop. */
const RELOAD_ATTEMPTS_KEY = "deploymentReloadAttempts";

/**
 * After this many reloads that did not put the tab on the deployed build, reloading is
 * given up: something between the tab and the web server keeps answering with the old
 * build and further reloads would only repeat every countdown.
 */
const MAX_RELOAD_ATTEMPTS = 2;

/**
 * Recognizes a new deployment and brings the tab onto it.
 *
 * A tab whose build differs from the deployed one is stale: the web server only serves
 * the files of the newest build, so sooner or later it fails to load a chunk. It
 * therefore has to reload - the only question is when. Reloading unnoticed is preferred
 * over asking, so the reload happens at the first moment that costs the user nothing:
 * while the tab is still fresh, or as soon as it is in the background. Only a tab that
 * is visible and in use gets the countdown in the header.
 */
class DeploymentService {
  private readonly ownBuildId = __BUILD_ID__;

  private loadedAt = Date.now();
  private userInteracted = false;
  private reloading = false;

  /** Build the web server serves now, empty as long as this tab runs the deployed one. */
  private deployedBuildId = "";

  private countdown: ReturnType<typeof setInterval> | null = null;

  /** Called once from main.ts, before the service worker is even registered. */
  public start(): void {
    if (environment === "development") {
      return;
    }

    ["pointerdown", "keydown"].forEach(event =>
      window.addEventListener(event, () => (this.userInteracted = true), { capture: true, passive: true }),
    );

    // Check on both directions: coming back to a tab is the case in which it has been
    // open for days, and leaving a tab is the moment at which it can reload unseen.
    document.addEventListener("visibilitychange", () => this.check());
    window.addEventListener("online", () => this.check());
    window.addEventListener("pageshow", event => {
      if ((event as PageTransitionEvent).persisted) {
        // Restored from the back/forward cache. The page may have been suspended for
        // days, but for the user it just opened - so reloading it now goes unnoticed.
        this.loadedAt = Date.now();
        this.userInteracted = false;
        this.check();
      }
    });

    // Another tab already found the new build. Reacting to it here saves this tab the
    // wait for its own next poll. Only forward: a tab that is behind must never pull a
    // tab that is already on the deployed build backwards.
    appLocalStorage.onChange<string>(DEPLOYED_BUILD_ID_KEY, deployedBuildId => {
      if (deployedBuildId && this.isNewerThanOwn(deployedBuildId)) {
        this.onDeployedBuild(deployedBuildId);
      }
    });

    setInterval(() => this.check(), CHECK_INTERVAL_MS);
    this.check();
  }

  /**
   * Reported by the service worker: either by the one that just activated with a new
   * build, or - right after a deployment, while the new one is still installing - by the
   * previous one, which is still in control and still answers with its own build.
   *
   * A service worker only ever knows the build of its own cache generation. It can point
   * out that something newer exists, but it can never tell this page that it runs the
   * wrong build: a tab opened after a deployment already runs a build newer than the
   * service worker controlling it, and reloading it would change nothing.
   */
  public onServiceWorkerBuild(buildId: string): void {
    if (this.isNewerThanOwn(buildId)) {
      this.onDeployedBuild(buildId);

      return;
    }

    if (this.isOlderThanOwn(buildId)) {
      // This page is ahead of the service worker that controls it. Nothing to reload,
      // but the worker should catch up, so that the build this page runs gets precached
      // and the outdated cache generation is retired.
      this.checkForServiceWorkerUpdate();
    }
  }

  /** The user pressed "Update Now" - an explicit decision, so this reloads right away. */
  public reloadNow(): void {
    this.reload();
  }

  private async check(): Promise<void> {
    if (store.offline.isOffline || !navigator.onLine || this.reloading) {
      return;
    }

    const deployedBuildId = await this.fetchDeployedBuildId();
    if (!deployedBuildId) {
      return;
    }

    if (this.isStale(deployedBuildId)) {
      await this.onDeployedBuild(deployedBuildId);

      return;
    }

    this.onOwnBuildDeployed();
  }

  /**
   * version.json is the only authoritative answer to what the web server serves, and it
   * just named the build this tab runs. Whatever concluded otherwise before was wrong,
   * so take it all back: the countdown, the announcement in the header and the reload
   * attempts that would otherwise make the next deployment give up too early.
   */
  private onOwnBuildDeployed(): void {
    this.deployedBuildId = "";
    appSessionStorage.removeItem(RELOAD_ATTEMPTS_KEY);

    if (this.countdown !== null) {
      clearInterval(this.countdown);
      this.countdown = null;
    }

    store.deployment.withdrawUpdate();
  }

  private async fetchDeployedBuildId(): Promise<string> {
    try {
      // "no-store" bypasses the HTTP cache: without it, browsers may serve a stale
      // version.json (heuristic caching) and new deployments stay undetected for days
      const response = await fetch("/version.json", { cache: "no-store" });

      return ((await response.json()) as DeployedBuild).buildId || "";
    } catch (e) {
      console.error("Could not fetch version...");
      console.error(e);

      return "";
    }
  }

  private async onDeployedBuild(deployedBuildId: string): Promise<void> {
    if (this.reloading || !this.isStale(deployedBuildId)) {
      return;
    }

    if (this.deployedBuildId !== deployedBuildId) {
      this.deployedBuildId = deployedBuildId;
      appLocalStorage.setItem(DEPLOYED_BUILD_ID_KEY, deployedBuildId);

      // Let the new service worker install and precache in the background, so the new
      // build is complete in the cache (and usable offline) by the time the tab reloads.
      this.checkForServiceWorkerUpdate();
    }

    if (this.reloadAttemptsFor(deployedBuildId) >= MAX_RELOAD_ATTEMPTS) {
      this.announce(null);

      return;
    }

    // An upload must never be interrupted, and it is not worth announcing either: by the
    // time it is done, the next check decides again and may well reload unnoticed.
    if (!(await this.isReloadSafe())) {
      return;
    }

    if (this.isUnnoticedReloadPossible()) {
      this.reload();

      return;
    }

    this.announce(RELOAD_COUNTDOWN_SECONDS);
  }

  /**
   * A different build id means stale, no matter in which direction: what counts is
   * matching the build the web server actually serves, which includes a rollback.
   * Without a build id on either side nothing can be told apart, so nothing happens.
   */
  private isStale(deployedBuildId: string): boolean {
    return !!deployedBuildId && !!this.ownBuildId && deployedBuildId !== this.ownBuildId;
  }

  /**
   * Whether a build was made after / before the one this tab runs. Build ids start with a
   * sortable UTC timestamp (see scripts/build-id.sh), so they compare as strings.
   */
  private isNewerThanOwn(buildId: string): boolean {
    return !!buildId && !!this.ownBuildId && buildId > this.ownBuildId;
  }

  private isOlderThanOwn(buildId: string): boolean {
    return !!buildId && !!this.ownBuildId && buildId < this.ownBuildId;
  }

  private checkForServiceWorkerUpdate(): void {
    ServiceWorkerHelper.checkForUpdate().catch(e => {
      console.error("Could not check for a new service worker");
      console.error(e);
    });
  }

  private isUnnoticedReloadPossible(): boolean {
    // The tab is in the background: the user does not see the reload at all.
    if (document.hidden) {
      return true;
    }

    // The page has just been opened and nothing has been done in it yet, so a reload
    // only looks like a page load that took a moment longer.
    return !this.userInteracted && Date.now() - this.loadedAt < UNNOTICED_RELOAD_TIMEOUT_MS;
  }

  private async isReloadSafe(): Promise<boolean> {
    if (volateqApi.isUploading) {
      return false;
    }

    if (!store.auth.isAuthenticated) {
      return true;
    }

    try {
      await userService.refreshMe();

      return !(await userService.me()).is_uploading;
    } catch (e) {
      console.error("Could not check for running uploads. Postponing the reload...");
      console.error(e);

      return false;
    }
  }

  /** How often this tab already reloaded for that build without ever reaching it. */
  private reloadAttemptsFor(deployedBuildId: string): number {
    const attempts = appSessionStorage.getItem<ReloadAttempts>(RELOAD_ATTEMPTS_KEY);

    return attempts && attempts.buildId === deployedBuildId ? attempts.count : 0;
  }

  /**
   * Announces the update in the header. With "countdownSeconds" the tab reloads itself
   * when the countdown ran out, without it the user is only offered the "Update Now"
   * button. Announcing twice would restart the countdown, so the first one wins.
   */
  private announce(countdownSeconds: number | null): void {
    if (store.deployment.isUpdateAvailable) {
      return;
    }

    store.deployment.announceUpdate(countdownSeconds);

    if (countdownSeconds === null) {
      return;
    }

    this.countdown = setInterval(() => {
      if (store.deployment.remainingSeconds !== 0) {
        store.deployment.countDown();

        return;
      }

      // An upload that this tab started after the countdown began: wait for it instead
      // of interrupting it. Uploads of the other tabs survive the reload of this one.
      if (volateqApi.isUploading) {
        return;
      }

      clearInterval(this.countdown!);
      this.countdown = null;
      this.reload();
    }, 1000);
  }

  private reload(): void {
    if (this.reloading) {
      return;
    }
    this.reloading = true;

    appSessionStorage.setItem<ReloadAttempts>(RELOAD_ATTEMPTS_KEY, {
      buildId: this.deployedBuildId,
      count: this.reloadAttemptsFor(this.deployedBuildId) + 1,
    });

    ReloadHelper.forceReload();
  }
}

export const deploymentService = new DeploymentService();
